import React, { Component } from 'react';
import {
    DetailsList,
    DetailsListLayoutMode,
    IColumn,
    SelectionMode,
    Selection,
    IconButton,
    CommandButton,
    SearchBox,
    Dropdown,
    IDropdownOption,
    Toggle,
    Dialog,
    DialogType,
    DialogFooter,
    PrimaryButton,
    DefaultButton,
    Spinner
} from '@fluentui/react';
import {
    IFileProvisionConfig,
    DestinationType,
    getDestinationTypeColor,
    destinationOptions
} from './types';
import NoRecordsFound from '../../../../../core/components/NoRecordsFound';
import { deleteBulkConfigsService, toggleProvisionerStatusService, triggerWebjob } from './services';
import { IFileProvisionerState } from './FileProvisioner';

interface IProps {
    configs: IFileProvisionConfig[];
    onEditConfig: (config: IFileProvisionConfig) => void;
    onViewDetails: (config: IFileProvisionConfig) => void;
    onSetState: (propertyName: keyof IFileProvisionerState, value: any) => void;
}

interface IState {
    filteredConfigs: IFileProvisionConfig[];
    searchTerm: string;
    destinationFilter: string | null;
    activeFilter: 'all' | 'active' | 'inactive';
    selectionDetails: string;
    dialogConfig: {
        hidden: boolean;
        title: string;
        message: string;
        type: 'success' | 'error' | 'info';
    };
    showConfirmDialog: boolean;
    confirmDialogTitle: string;
    confirmDialogMessage: string;
    pendingConfirmAction: (() => void) | null;
    isProcessing: boolean;
}

class ProvisionConfigTable extends Component<IProps, IState> {
    private _selection: Selection;

    constructor(props: IProps) {
        super(props);

        this._selection = new Selection({
            onSelectionChanged: () => {
                this.setState({
                    selectionDetails: this._getSelectionDetails(),
                });
            },
            getKey: this._getKey,
        });

        this.state = {
            filteredConfigs: [],
            searchTerm: '',
            destinationFilter: null,
            activeFilter: 'all',
            selectionDetails: '',
            dialogConfig: {
                hidden: true,
                title: '',
                message: '',
                type: 'info'
            },
            showConfirmDialog: false,
            confirmDialogTitle: '',
            confirmDialogMessage: '',
            pendingConfirmAction: null,
            isProcessing: false
        };
    }

    componentDidUpdate(prevProps: Readonly<IProps>, prevState: Readonly<IState>, snapshot?: any): void {
        if (prevProps.configs !== this.props.configs) {
            this._applyFilters();
        }
    }
    private _getSelectionDetails(): string {
        const selectionCount = this._selection.getSelectedCount();
        switch (selectionCount) {
            case 0:
                return 'No items selected';
            case 1:
                return '1 item selected';
            default:
                return `${selectionCount} items selected`;
        }
    }

    private _getKey(item: any, index?: number): string {
        return item.id?.toString() || index?.toString() || '0';
    }

    private _onSearchChange = (event?: React.ChangeEvent<HTMLInputElement>, newValue?: string): void => {
        this.setState({ searchTerm: newValue || '' }, this._applyFilters);
    };

    private _onSearchClear = (): void => {
        this.setState({ searchTerm: '' }, this._applyFilters);
    };

    private _onDestinationFilterChange = (event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption): void => {
        const destinationFilter = option?.key !== undefined && option?.key !== '' ? (option.key as string) : null;
        this.setState({ destinationFilter }, this._applyFilters);
    };

    private _onActiveFilterChange = (event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption): void => {
        const activeFilter = (option?.key as 'all' | 'active' | 'inactive') || 'all';
        this.setState({ activeFilter }, this._applyFilters);
    };

    private _applyFilters = (): void => {
        const { searchTerm, destinationFilter, activeFilter } = this.state;
        const { configs } = this.props;
        let filtered = [...configs];

        // Apply search filter
        if (searchTerm.trim()) {
            const lowerSearchTerm = searchTerm.toLowerCase();
            filtered = filtered.filter(config =>
                config.destinationType.toLowerCase().includes(lowerSearchTerm) ||
                (config.notes && config.notes.toLowerCase().includes(lowerSearchTerm)) ||
                (config.targetUri && config.targetUri.toLowerCase().includes(lowerSearchTerm))
            );
        }

        // Apply destination filter
        if (destinationFilter) {
            filtered = filtered.filter(config => config.destinationType === destinationFilter);
        }

        // Apply active status filter
        if (activeFilter === 'active') {
            filtered = filtered.filter(config => config.isActive);
        } else if (activeFilter === 'inactive') {
            filtered = filtered.filter(config => !config.isActive);
        }

        this.setState({ filteredConfigs: filtered });
    };

    private _onAddConfig = (): void => {
        const newConfig: IFileProvisionConfig = {
            id: 0,
            authType: "OAuth2",
            credentialsReference: "AppRegistration:FEBER_DataProvisioner,ClientSecret:Graph_API",
            destinationType: DestinationType.sharepoint,
            rawQuery: 'Configured by administrator',
            isActive: true,
            lastRunAt: null
        };
        this.props.onEditConfig(newConfig);
    };

    private _onDeleteSelected = (): void => {
        const selectedConfigs = this._selection.getSelection() as IFileProvisionConfig[];
        console.log(selectedConfigs);
        if (selectedConfigs.length > 0) {
            const title = selectedConfigs.length === 1 ? 'Delete Configuration' : `Delete ${selectedConfigs.length} Configurations`;
            const message = `Are you sure you want to delete the selected ${selectedConfigs.length} config(s)? This action cannot be undone.`;

            this.setState({
                showConfirmDialog: true,
                confirmDialogTitle: title,
                confirmDialogMessage: message,
                pendingConfirmAction: () => this._executeDelete(selectedConfigs)
            });
        }
    };

    private _executeDelete = async (selectedConfigs: IFileProvisionConfig[]): Promise<void> => {
        this.setState({ isProcessing: true });
        try {
            const callDeleteConfig = await deleteBulkConfigsService(selectedConfigs.map(c => c.id));
            if (!callDeleteConfig) {
                this.setState({
                    dialogConfig: {
                        hidden: false,
                        title: 'Delete Failed',
                        message: 'Failed to delete selected configs. Please try again.',
                        type: 'error'
                    },
                    isProcessing: false
                });
                this._closeConfirmDialog();
                return;
            }
            const remainingConfigs = this.props.configs.filter(config =>
                !selectedConfigs.some(selected => selected.id === config.id)
            );
            this.props.onSetState('configs', remainingConfigs);
            this._selection.setAllSelected(false);
            this._applyFilters();
            this.setState({ isProcessing: false });
            this._closeConfirmDialog();
        } catch (error) {
            console.error('Failed to delete configs:', error);
            this.setState({
                dialogConfig: {
                    hidden: false,
                    title: 'Delete Failed',
                    message: 'Failed to delete selected configs. Please try again.',
                    type: 'error'
                },
                isProcessing: false
            });
            this._closeConfirmDialog();
        }
    };

    private _onToggleActive = async (config: IFileProvisionConfig): Promise<void> => {
        try {
            const callToggle = await toggleProvisionerStatusService(config.id, !config.isActive);
            if (!callToggle) {
                alert('Failed to toggle active status. Please try again.');
                return;
            }
            const updatedConfigs = this.props.configs.map(c =>
                c.id === config.id ? { ...c, isActive: !c.isActive } : c
            );
            this.props.onSetState('configs', updatedConfigs);
            this._applyFilters();
        } catch (error) {
            console.error('Failed to toggle active status:', error);
        }
    };

    private _onTriggerRunAll = (): void => {
        const activeConfigs = this.props.configs.filter(c => c.isActive);
        if (activeConfigs.length === 0) {
            this.setState({
                dialogConfig: {
                    hidden: false,
                    title: 'No Active Configurations',
                    message: 'No active configurations to run.',
                    type: 'info'
                }
            });
            return;
        }

        this.setState({
            showConfirmDialog: true,
            confirmDialogTitle: 'Trigger WebJob',
            confirmDialogMessage: `Are you sure you want to trigger the WebJob? It will run all ${activeConfigs.length} active configuration(s).`,
            pendingConfirmAction: () => this._executeTriggerWebJob(activeConfigs)
        });
    };

    private _executeTriggerWebJob = async (activeConfigs: IFileProvisionConfig[]): Promise<void> => {
        this.setState({ isProcessing: true });
        try {
            const result = await triggerWebjob();
            if (result) {
                this.setState({
                    dialogConfig: {
                        hidden: false,
                        title: 'WebJob Triggered',
                        message: `WebJob triggered successfully! Running ${activeConfigs.length} active configuration(s).`,
                        type: 'success'
                    },
                    isProcessing: false
                });
                // Update lastRunAt for all active configs
                const now = new Date();
                const updatedConfigs = this.props.configs.map(c =>
                    c.isActive ? { ...c, lastRunAt: now } : c
                );
                this.props.onSetState('configs', updatedConfigs);
                this._applyFilters();
                this._closeConfirmDialog();
            } else {
                this.setState({
                    dialogConfig: {
                        hidden: false,
                        title: 'Trigger Failed',
                        message: 'Failed to trigger WebJob. Please try again.',
                        type: 'error'
                    },
                    isProcessing: false
                });
                this._closeConfirmDialog();
            }
        } catch (error) {
            console.error('Failed to trigger WebJob:', error);
            this.setState({
                dialogConfig: {
                    hidden: false,
                    title: 'Trigger Failed',
                    message: 'Failed to trigger WebJob. Please try again.',
                    type: 'error'
                },
                isProcessing: false
            });
            this._closeConfirmDialog();
        }
    };

    private _closeConfirmDialog = (): void => {
        this.setState({
            showConfirmDialog: false,
            confirmDialogTitle: '',
            confirmDialogMessage: '',
            pendingConfirmAction: null
        });
    };

    private _confirmAction = (): void => {
        const { pendingConfirmAction } = this.state;
        if (pendingConfirmAction) {
            pendingConfirmAction();
        }
    };

    private _columns: IColumn[] = [
        {
            key: 'destinationType',
            name: 'Destination',
            fieldName: 'destinationType',
            minWidth: 120,
            maxWidth: 150,
            isResizable: true,
            onRender: (item: IFileProvisionConfig) => {
                const color = getDestinationTypeColor(item.destinationType);
                return (
                    <div style={{
                        display: 'inline-block',
                        padding: '4px 12px',
                        borderRadius: '12px',
                        backgroundColor: `${color}20`,
                        color: color,
                        fontWeight: 600,
                        fontSize: '12px',
                        border: `1px solid ${color}40`
                    }}>
                        {item.destinationType}
                    </div>
                );
            }
        },
        {
            key: 'target',
            name: 'Target Location',
            minWidth: 250,
            maxWidth: 350,
            isResizable: true,
            onRender: (item: IFileProvisionConfig) => {
                let targetText = '';
                if (item.destinationType === DestinationType.sharepoint) {
                    targetText = item.folderPath || item.siteUrl || 'N/A';
                } else if (item.destinationType === DestinationType.blobContainer) {
                    targetText = item.targetUri || item.containerName || 'N/A';
                } else {
                    targetText = item.targetUri || 'N/A';
                }
                return (
                    <div style={{
                        fontSize: '12px',
                        color: '#323130',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        padding: '8px 0',
                        lineHeight: '1.5'
                    }} title={targetText}>
                        {targetText}
                    </div>
                );
            }
        },
        {
            key: 'lastRun',
            name: 'Last Run',
            fieldName: 'lastRunAt',
            minWidth: 120,
            maxWidth: 140,
            isResizable: true,
            onRender: (item: IFileProvisionConfig) => {
                if (!item.lastRunAt) {
                    return <span style={{ fontSize: '12px', color: '#a19f9d' }}>Never</span>;
                }
                const date = new Date(item.lastRunAt + 'Z');
                const now = new Date();
                const diffHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));

                let timeText = '';
                if (diffHours < 1) {
                    timeText = 'Just now';
                } else if (diffHours < 24) {
                    timeText = `${diffHours}h ago`;
                } else {
                    timeText = date.toLocaleDateString();
                }

                return (
                    <div style={{ fontSize: '13px', color: '#605e5c' }} title={date.toLocaleString()}>
                        {timeText}
                    </div>
                );
            }
        },
        {
            key: 'status',
            name: 'Status',
            fieldName: 'isActive',
            minWidth: 80,
            maxWidth: 90,
            isResizable: true,
            onRender: (item: IFileProvisionConfig) => {
                return (
                    <Toggle
                        checked={item.isActive}
                        onChange={() => this._onToggleActive(item)}
                        styles={{
                            root: { marginBottom: 0 }
                        }}
                    />
                );
            }
        },
        {
            key: 'actions',
            name: 'Actions',
            minWidth: 100,
            maxWidth: 100,
            onRender: (item: IFileProvisionConfig) => {
                return (
                    <div style={{ display: 'flex', gap: '4px' }}>
                        <IconButton
                            iconProps={{ iconName: 'Edit' }}
                            title="Edit Config"
                            ariaLabel="Edit Config"
                            onClick={() => this.props.onEditConfig(item)}
                        />
                        <IconButton
                            iconProps={{ iconName: 'View' }}
                            title="View Details"
                            ariaLabel="View Details"
                            onClick={() => this.props.onViewDetails(item)}
                            styles={{
                                icon: { color: '#0078d4' }
                            }}
                        />
                    </div>
                );
            }
        }
    ];

    private _renderHeaderActions = (): JSX.Element => {
        const { destinationFilter, activeFilter } = this.state;

        const destinationDropdownOptions: IDropdownOption[] = [
            { key: '', text: 'All Destinations' },
            ...destinationOptions
        ];

        const activeOptions: IDropdownOption[] = [
            { key: 'all', text: 'All Status' },
            { key: 'active', text: 'Active Only' },
            { key: 'inactive', text: 'Inactive Only' }
        ];

        return (
            <div style={{
                backgroundColor: '#f8f9fa',
                borderBottom: '1px solid #edebe9',
                padding: '12px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '12px'
            }}>
                <div>
                    <h3 style={{ margin: '0 0 8px 0' }}>File Provision Configurations</h3>
                    <div style={{ fontSize: '13px', color: '#605e5c' }}>
                        Manage automated file provisioning jobs to various destinations
                    </div>
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <SearchBox
                        placeholder="Search configs..."
                        underlined={true}
                        value={this.state.searchTerm}
                        onChange={this._onSearchChange}
                        onClear={this._onSearchClear}
                        styles={{ root: { width: '200px' } }}
                    />
                    <Dropdown
                        placeholder="Destination"
                        options={destinationDropdownOptions}
                        selectedKey={destinationFilter || ''}
                        onChange={this._onDestinationFilterChange}
                        styles={{ root: { width: '140px' } }}
                    />
                    <Dropdown
                        placeholder="Status"
                        options={activeOptions}
                        selectedKey={activeFilter}
                        onChange={this._onActiveFilterChange}
                        styles={{ root: { width: '120px' } }}
                    />
                    <CommandButton
                        iconProps={{ iconName: 'Add' }}
                        text="Add Config"
                        onClick={this._onAddConfig}
                        styles={{
                            root: { color: '#0078d4', fontWeight: 600 },
                            icon: { color: '#0078d4' }
                        }}
                    />
                    <CommandButton
                        iconProps={{ iconName: 'Play' }}
                        text="Run WebJob"
                        onClick={this._onTriggerRunAll}
                        styles={{
                            root: { color: '#107c10', fontWeight: 600 },
                            icon: { color: '#107c10' }
                        }}
                    />
                    <CommandButton
                        iconProps={{ iconName: 'Delete' }}
                        text="Delete Selected"
                        onClick={this._onDeleteSelected}
                        styles={{
                            root: { color: '#d13438', fontWeight: 600 },
                            icon: { color: '#d13438' }
                        }}
                    />
                </div>
            </div>
        );
    };

    render() {
        const { filteredConfigs, dialogConfig } = this.state;

        return (
            <div style={{ height: '700px', display: 'block' }}>
                {this._renderHeaderActions()}

                {filteredConfigs.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                        <NoRecordsFound />
                    </div>
                ) : (
                    <div style={{
                        height: 'calc(100% - 110px)',
                        overflow: 'auto',
                        border: '1px solid #edebe9',
                        display: 'block',
                        position: 'relative'
                    }}>
                        <DetailsList
                            items={filteredConfigs}
                            columns={this._columns}
                            selectionMode={SelectionMode.multiple}
                            setKey="configs"
                            layoutMode={DetailsListLayoutMode.justified}
                            isHeaderVisible={true}
                            selection={this._selection}
                            selectionPreservedOnEmptyClick={true}
                            styles={{
                                root: {
                                    '.ms-DetailsHeader': {
                                        position: 'sticky',
                                        top: 0,
                                        zIndex: 100,
                                        backgroundColor: '#ffffff'
                                    }
                                }
                            }}
                        />
                    </div>
                )}

                {/* Confirmation Dialog */}
                <Dialog
                    hidden={!this.state.showConfirmDialog}
                    onDismiss={this._closeConfirmDialog}
                    dialogContentProps={{
                        type: DialogType.normal,
                        title: this.state.confirmDialogTitle,
                        subText: this.state.confirmDialogMessage
                    }}
                    modalProps={{
                        isBlocking: true,
                        styles: { main: { maxWidth: 450 } }
                    }}
                >
                    {this.state.isProcessing && (
                        <Spinner label="Processing..." ariaLive="assertive" labelPosition="right" />
                    )}
                    <DialogFooter>
                        <PrimaryButton
                            onClick={this._confirmAction}
                            text="Confirm"
                            disabled={this.state.isProcessing}
                            styles={{
                                root: { backgroundColor: '#0078d4' },
                                rootHovered: { backgroundColor: '#106ebe' },
                                rootPressed: { backgroundColor: '#005a9e' }
                            }}
                        />
                        <DefaultButton
                            onClick={this._closeConfirmDialog}
                            text="Cancel"
                            disabled={this.state.isProcessing}
                        />
                    </DialogFooter>
                </Dialog>

                {/* Info/Success/Error Dialog */}
                <Dialog
                    hidden={dialogConfig.hidden}
                    onDismiss={() => this.setState({ dialogConfig: { ...dialogConfig, hidden: true } })}
                    dialogContentProps={{
                        type: DialogType.normal,
                        title: dialogConfig.title,
                        subText: dialogConfig.message
                    }}
                    modalProps={{
                        isBlocking: false,
                        styles: { main: { maxWidth: 450 } }
                    }}
                >
                    <DialogFooter>
                        <PrimaryButton
                            onClick={() => this.setState({ dialogConfig: { ...dialogConfig, hidden: true } })}
                            text="OK"
                        />
                    </DialogFooter>
                </Dialog>
            </div>
        );
    }
}

export default ProvisionConfigTable;
